/**
 * Where a run goes next (blueprint section 4), over the pieces this build ships, in the script's order. Pure: it
 * never imports the Redux store, so jest tests it directly. Port of ows-ftux brand-ftue/progression.ts by piece id
 * (the prototype held an index), with each piece's availability in place of the prototype's blog switch.
 */
import { BRAND_PIECE_IDS, SKIP_REASONS, type BrandPieceId, type FtuxOutcome, type SkipReason } from '../catalog';
import type { FtuxProgress, PieceAvailability, SkippedPiece } from './types';

/** Each piece's availability as its probe last reported it (none yet reads as checking). */
export type AvailabilityMap = Partial<Record<BrandPieceId, PieceAvailability>>;

/** The run after a move, and the pieces the flow passed by on the way (each one's `ended skipped`). */
export type ProgressStep = { progress: FtuxProgress; skipped: SkippedPiece[] };

/** Why a piece can never be offered, or null when it can be (or may be). */
export const skipReasonOf = (availability: PieceAvailability | undefined): SkipReason | null =>
  SKIP_REASONS.find((reason) => availability === `never:${reason}`) ?? null;

/** `progress` with `piece` ended as `outcome`. Pure. */
export const withOutcome = (progress: FtuxProgress, piece: BrandPieceId, outcome: FtuxOutcome): FtuxProgress => ({
  ...progress,
  outcomes: { ...progress.outcomes, [piece]: outcome },
});

const availabilityOf = (availability: AvailabilityMap, id: BrandPieceId): PieceAvailability =>
  availability[id] ?? 'checking';

/** The pieces after `from` in the script's order (all of them from the start). */
const piecesAfter = (from: BrandPieceId | null): readonly BrandPieceId[] =>
  from === null ? BRAND_PIECE_IDS : BRAND_PIECE_IDS.slice(BRAND_PIECE_IDS.indexOf(from) + 1);

const waitingOn = (progress: FtuxProgress, piece: BrandPieceId, deferred: BrandPieceId[]): FtuxProgress => ({
  ...progress,
  status: 'waiting',
  piece,
  deferred,
});

/**
 * Where the run goes once the piece at `from` is done or let go (`from` null: from the start, after the map):
 *  1. a deferred piece that is ready now comes back first;
 *  2. else the next shipped piece after `from` with no outcome and not deferred: one that can never be offered ends
 *     skipped on the way, one that is not ready yet is passed by and deferred, one that is ready or still checking
 *     waits (its card hidden until it is ready);
 *  3. else the first deferred piece waits, its card hidden until it is ready;
 *  4. else the run is finished.
 * Ids this build does not ship are never offered and stay where they are. Pure.
 */
export const progressAfter = (
  progress: FtuxProgress,
  from: BrandPieceId | null,
  shipped: readonly BrandPieceId[],
  availability: AvailabilityMap,
): ProgressStep => {
  const isShipped = (id: BrandPieceId): boolean => shipped.includes(id);
  const stateOf = (id: BrandPieceId): PieceAvailability => availabilityOf(availability, id);
  const { deferred } = progress;
  const back = deferred.find((id) => isShipped(id) && stateOf(id) === 'ready');
  if (back) {
    const rest = deferred.filter((id) => id !== back);
    return { progress: waitingOn(progress, back, rest), skipped: [] };
  }

  const open = piecesAfter(from).filter(
    (id) => isShipped(id) && progress.outcomes[id] === undefined && !deferred.includes(id),
  );
  const next = open.findIndex((id) => stateOf(id) === 'ready' || stateOf(id) === 'checking');
  const passedBy = next === -1 ? open : open.slice(0, next);
  const skipped = passedBy.flatMap((id): SkippedPiece[] => {
    const reason = skipReasonOf(stateOf(id));
    return reason ? [{ id, reason }] : [];
  });
  const moved = skipped.reduce((run, { id }) => withOutcome(run, id, 'skipped'), progress);
  const later = [...deferred, ...passedBy.filter((id) => stateOf(id) === 'not-yet')];
  if (next !== -1) return { progress: waitingOn(moved, open[next], later), skipped };

  const first = later.find(isShipped);
  if (first) {
    const rest = later.filter((id) => id !== first);
    return { progress: waitingOn(moved, first, rest), skipped };
  }
  return { progress: { ...moved, status: 'finished', piece: null, deferred: [] }, skipped };
};

/**
 * A deferred piece that is ready now and comes before `piece` in the script's order, back in its place: it waits on
 * Spotlight, and the piece it displaced (no outcome, not deferred) comes again after it in the script's order. Null
 * when there is none.
 */
const deferredBackBefore = (
  progress: FtuxProgress,
  piece: BrandPieceId,
  shipped: readonly BrandPieceId[],
  availability: AvailabilityMap,
): ProgressStep | null => {
  const at = BRAND_PIECE_IDS.indexOf(piece);
  const back = progress.deferred.find(
    (id) => shipped.includes(id) && availabilityOf(availability, id) === 'ready' && BRAND_PIECE_IDS.indexOf(id) < at,
  );
  if (!back) return null;
  const rest = progress.deferred.filter((id) => id !== back);
  return { progress: waitingOn(progress, back, rest), skipped: [] };
};

/**
 * The waiting piece checked against what can be offered now (while it waits on Spotlight, nothing open), or null
 * when nothing changes:
 *  - a piece this build does not know or ship (a record from another build): the run moves on from it;
 *  - never: it ends skipped, the run moves on;
 *  - a deferred piece earlier in the script is ready now (the blog turned on while Mail waits): it comes back in the
 *    waiting piece's place, which comes again after it (the owner never reloads for it);
 *  - not yet: it is deferred, only when another piece can take its place (one ready or still checking, never a
 *    piece that is not ready yet itself, or two such pieces would take turns forever).
 * Pure.
 */
export const reconcileWaiting = (
  progress: FtuxProgress,
  shipped: readonly BrandPieceId[],
  availability: AvailabilityMap,
): ProgressStep | null => {
  if (progress.status !== 'waiting') return null;
  const { piece } = progress;
  if (piece === null || !shipped.includes(piece)) return progressAfter(progress, piece, shipped, availability);

  const now = availabilityOf(availability, piece);
  const reason = skipReasonOf(now);
  if (reason) {
    const next = progressAfter(withOutcome(progress, piece, 'skipped'), piece, shipped, availability);
    return { progress: next.progress, skipped: [{ id: piece, reason }, ...next.skipped] };
  }
  const back = deferredBackBefore(progress, piece, shipped, availability);
  if (back) return back;
  if (now !== 'not-yet') return null;

  const next = progressAfter({ ...progress, deferred: [...progress.deferred, piece] }, piece, shipped, availability);
  const taker = next.progress.piece;
  return taker !== null && taker !== piece && availabilityOf(availability, taker) !== 'not-yet' ? next : null;
};
